import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { useKnowledgeBaseCore } from './useKnowledgeBaseCore';
import { generateEmbedding } from '@/lib/embeddings';

export function useKnowledgeBases() {
  const knowledgeBaseCore = useKnowledgeBaseCore();
  const { user } = useAuth();
  const { toast } = useToast();
  const [sharesLoading, setSharesLoading] = useState(false);

  const addDocument = useCallback(async (knowledgeBaseId: string, document: {
    name: string;
    content: string;
    fileType?: string;
    size: number;
  }) => {
    try {
      const embedding = await generateEmbedding(document.content);
      const payload: Record<string, unknown> = {
        knowledge_base_id: knowledgeBaseId,
        name: document.name,
        content: document.content,
        file_type: document.fileType || 'markdown',
        size: document.size,
      };

      if (embedding) {
        payload.embedding = embedding;
      }

      const { data, error } = await (supabase as any)
        .from('knowledge_base_documents')
        .insert(payload)
        .select('*')
        .single();

      if (error) throw error;

      return (data as { id: string }).id;
    } catch (error: any) {
      console.error('Error adding document:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to add document',
        variant: 'destructive',
      });
      return null;
    }
  }, [toast]);

  const updateDocument = useCallback(async (documentId: string, updates: {
    name?: string;
    content?: string;
    size?: number;
  }) => {
    try {
      const updateData: Record<string, unknown> = {
        ...updates,
        updated_at: new Date().toISOString(),
      };

      if (updates.content) {
        const embedding = await generateEmbedding(updates.content);
        if (embedding) {
          updateData.embedding = embedding;
        }
      }

      const { error } = await (supabase as any)
        .from('knowledge_base_documents')
        .update(updateData)
        .eq('id', documentId);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error updating document:', error);
      toast({
        title: 'Error',
        description: 'Failed to update document',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast]);

  const deleteDocument = useCallback(async (documentId: string) => {
    try {
      const { error } = await supabase
        .from('knowledge_base_documents')
        .delete()
        .eq('id', documentId);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete document',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast]);

  const getKnowledgeBaseShares = useCallback(async (knowledgeBaseId: string) => {
    try {
      setSharesLoading(true);

      const { data, error } = await supabase
        .from('knowledge_base_shares')
        .select('*')
        .eq('knowledge_base_id', knowledgeBaseId);

      if (error) throw error;

      const shares = (data ?? []) as Array<{
        id: string;
        user_id: string;
        access_level: string;
      }>;

      const userIds = [...new Set(shares.map((share) => share.user_id))];
      if (userIds.length === 0) {
        return [];
      }

      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      const profileMap = new Map(
        ((profiles ?? []) as Array<{ id: string; full_name: string | null }>)
          .map((profile) => [profile.id, profile.full_name || 'Unknown User'])
      );

      return shares.map((share) => ({
        ...share,
        user_name: profileMap.get(share.user_id) || 'Unknown User',
        access_level: share.access_level as 'View' | 'Edit' | 'Manage',
      }));
    } catch (error) {
      console.error('Error fetching knowledge base shares:', error);
      return [];
    } finally {
      setSharesLoading(false);
    }
  }, []);

  const shareKnowledgeBase = useCallback(async (
    knowledgeBaseId: string,
    userId: string,
    accessLevel: 'View' | 'Edit' | 'Manage'
  ) => {
    if (!user) return false;

    try {
      const { data: existingShare, error: checkError } = await supabase
        .from('knowledge_base_shares')
        .select('*')
        .eq('knowledge_base_id', knowledgeBaseId)
        .eq('user_id', userId)
        .maybeSingle();

      if (checkError) throw checkError;

      if (existingShare) {
        const { error: updateError } = await (supabase as any)
          .from('knowledge_base_shares')
          .update({
            access_level: accessLevel,
            shared_by: user.id,
            shared_at: new Date().toISOString(),
          })
          .eq('id', (existingShare as { id: string }).id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await (supabase as any)
          .from('knowledge_base_shares')
          .insert({
            knowledge_base_id: knowledgeBaseId,
            user_id: userId,
            access_level: accessLevel,
            shared_by: user.id,
          });

        if (insertError) throw insertError;
      }

      return true;
    } catch (error) {
      console.error('Error sharing knowledge base:', error);
      toast({
        title: 'Error',
        description: 'Failed to share knowledge base',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast, user]);

  const updateShareAccessLevel = useCallback(async (
    shareId: string,
    accessLevel: 'View' | 'Edit' | 'Manage'
  ) => {
    try {
      const { error } = await (supabase as any)
        .from('knowledge_base_shares')
        .update({ access_level: accessLevel })
        .eq('id', shareId);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error updating access level:', error);
      toast({
        title: 'Error',
        description: 'Failed to update access level',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast]);

  const removeShare = useCallback(async (shareId: string) => {
    try {
      const { error } = await supabase
        .from('knowledge_base_shares')
        .delete()
        .eq('id', shareId);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error removing share:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove share',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast]);

  return {
    ...knowledgeBaseCore,
    addDocument,
    updateDocument,
    deleteDocument,
    getKnowledgeBaseShares,
    shareKnowledgeBase,
    updateShareAccessLevel,
    removeShare,
    sharesLoading,
  };
}